import { Alert } from 'components/Alert';
import dayjs from 'dayjs';
import { Nullable } from 'modules/types/common';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Action, Dispatch } from 'redux';
import { boostrapApp, PathType } from 'store/pageActions/bootstrap';
import { settingsErrorSelector, settingsLoadedSelector } from 'store/appSettings/selectors';
import { logout } from 'store/authentication/actions';
import { AuthResult, TpsStatus } from 'store/authentication/reducers';
import {
  dfdAuthenticationSelector,
  authenticationErrorSelector
} from 'store/authentication/selectors';
import { globalErrorSelector } from 'store/global/selectors';
import { navigationWebStateChange } from 'store/navigation/actions';
import { NavStateEnum } from 'store/navigation/types';
import { RootState } from 'store/types';
import { getPathtype } from './pathFinder';
import usePageVisibility from 'lib/hooks/usePageVisibility';

const loadStart = Date.now();

interface Props {
  authError: Error | null;
  apiKey: string;
  children?: React.ReactNode;
  dfdAuth?: AuthResult | null;
  getDepsError?: Nullable<Error>;
  hasSettings: boolean;
  hasFetchedDependencies: boolean;
  isAuthenticated: boolean;
  isFetchingDependencies: boolean;
  loadApp: typeof boostrapApp;
  logoutApp: typeof logout;
  pathAfterLogin?: string;
  pathname: string;
  setNavState: typeof navigationWebStateChange;
  settingsError: Nullable<Error>;
  tpsAuthStatus: TpsStatus[] | null;
  currentNavState?: NavStateEnum;
}

export function handleExpired(
  dfdAuth: AuthResult | null,
  dispatch: Dispatch<Action>,
  loadStart: number
) {
  // detect auth expired from inactivity.
  const tenMinutesSinceLoad = Date.now() - loadStart > 600000; // Ten minutes is not arbitrary. If an active user is using the app the refresh token will activate 10 minutes before the token expires.
  const expired =
    dfdAuth && !dayjs(dfdAuth.accessTokenExpirationDate).isAfter(dayjs().add(1, 'minute'));

  if (tenMinutesSinceLoad && expired) {
    localStorage.setItem('pathAfterLogin', '/u/dashboard');
    Alert.alert('Session Timeout', 'Your session has ended, please log in again.', [
      { text: 'OK', onPress: () => dispatch(logout()) }
    ]);
  }
}

/**
 * Method: NavState
 * Description: Handles loading error states including expired and kicks off boostrapApp if the app has not yet loaded.
 * @param param0
 */
export const NavState = ({
  authError,
  children,
  dfdAuth,
  getDepsError,
  hasSettings,
  pathname,
  settingsError
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isHidden = usePageVisibility();

  useEffect(() => {
    // Super hacks for CCBH and VPC
    if (pathname?.includes('connect-care/CCBH')) {
      window.localStorage.setItem('ccBrand', 'CCBH');
      pathname = pathname.replace('/CCBH', '');
    } else if (pathname?.includes('connect-care/VPC')) {
      window.localStorage.setItem('ccBrand', 'VPC');
      pathname = pathname.replace('/VPC', '');
    }
    const pathType: PathType = getPathtype(pathname);
    if (getDepsError) {
      Alert.alert(
        "We're sorry.",
        'There was an issue loading your information. If the problem persists please contact support',
        [{ text: 'OK', onPress: () => dispatch(logout()) }]
      );
      // Handle loading error
      // update state
    } else if (settingsError) {
      // check for settings error
      Alert.alert(
        'Failed to fetch app settings',
        'There was an issue fetching app settings. Check your network connection and try again.',
        [
          {
            text: 'Try Again',
            onPress: () => dispatch(boostrapApp({ pathname, pathType, history }))
          }
        ]
      );
      // update state
    } else if (authError) {
      Alert.alert('Failed to authenticate', 'Check your network connection and try again', [
        {
          onPress: () => dispatch(logout()),
          text: 'OK'
        }
      ]);
    } else if (!hasSettings) {
      dispatch(navigationWebStateChange('preload'));
      dispatch(boostrapApp({ pathname, pathType, history }));
    }
  }, [authError, getDepsError, hasSettings, settingsError]);

  useEffect(() => {
    handleExpired(dfdAuth, dispatch, loadStart);
  }, [dfdAuth, dispatch, isHidden]);
  return <>{children}</>;
};

const mapStateToProps = (state: RootState) => ({
  authError: authenticationErrorSelector(state),
  dfdAuth: dfdAuthenticationSelector(state),
  getDepsError: globalErrorSelector(state),
  hasSettings: settingsLoadedSelector(state),
  settingsError: settingsErrorSelector(state)
});

export default connect(mapStateToProps)(NavState);
